import { CloseOutlined } from '@ant-design/icons';
import type { RangePickerProps } from 'antd/es/date-picker';
import { type ChangeEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { Divider, Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import ScrollView from '@/components/Common/ScrollView';
import ToggleRadio from '@/components/Common/ToggleRadio';
import TransitionPortal from '@/components/Common/TransitionPortal';
import StyledDatePicker from '@/components/DatePicker';
import { AttrNumberInput } from '@/components/Inputs';
import FlexibleTextarea from '@/components/Inputs/FlexibleTextarea';
import FlexibleTitleInput from '@/components/Inputs/FlexibleTitle';
import RemarkInput from '@/components/Inputs/KeywordInput/RemarkInput';
import Modal from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import { AttrText } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import { NOT_ASSIGNED_LABEL } from '@/configs/reserved-word';
import CompanyDropdown from '@/features/Account/components/CompanyDropdown';
import AgentDropdown from '@/features/Agent/components/AgentDropdown';
import Bookmark, { type BookmarkModalState } from '@/features/Bookmark/Bookmark';
import CustomerDropdown from '@/features/Contact/CustomerDropdown';
import Attachment from '@/features/Feedback/FeedbackDetail/Attachment';
import DataOriginDropdown from '@/features/Feedback/field/DataOrigin/DataOriginDropdown';
import PriorityDropdown from '@/features/Feedback/field/Priority/PriorityDropdown';
import TagSelector from '@/features/Feedback/field/Tag/TagSelector';
import useColor from '@/hooks/domain/useColor';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { useInputs } from '@/hooks/pure/useInputs';
import useKeymap from '@/hooks/pure/useKeymap';
import useStates from '@/hooks/pure/useStates';
import service from '@/services/index';
import useCustomerStore from '@/stores/CustomerStore';
import useEditorStoreTemp from '@/stores/EditorStoreTemp';
import useFeedbackListStore from '@/stores/Feedback/FeedbackListStore';
import useDropdownStore from '@/stores/pure/DropdownStore';
import usePopupStore from '@/stores/pure/PopupStore';
import useToastStore from '@/stores/pure/ToastStore';
import useTagStore, { DEFAULT_SOURCE_MANUAL } from '@/stores/TagStore';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';
import { track } from '@/utils/track/track';
import { addQuery, generateRandomString } from '@/utils/util';

import BookmarkCreateModal from '../../../Bookmark/BookmarkModal/BookmarkCreateModal';

type State = {
  tagList: string[];
  company: CompanyT | null;
  customer: CustomerT | null;
  priority?: number;
  data_origin?: string;
  agent?: string;
  bookmarks: BookmarkT[];
  remarkList: string[];
};

type Props = {
  status: boolean;
  close: () => void;
};

const CreateFeedbackModal = (props: Props) => {
  const color = useColor();
  return (
    <TransitionPortal
      layerId="modal-layer"
      status={props.status}
      close={() => {}}
      background={{
        enable: true,
        style: {
          backdropFilter: 'blur(16px)',
          backgroundColor: color.FD20,
        },
      }}
      style={{
        top: 32,
        right: 32,
        height: 'calc(100vh - 64px)',
        width: 720,
      }}
    >
      <Content {...props} />
    </TransitionPortal>
  );
};

const Content = (props: Props) => {
  const color = useColor();
  const { navigate } = useWorkspaceRouter();
  const [searchParams] = useSearchParams();
  const {
    feedbackDesc: description,
    setFeedbackDesc: setDescription,
    createMore,
    setCreateMore,
  } = useEditorStoreTemp((state) => ({
    feedbackDesc: state.feedbackDesc,
    setFeedbackDesc: state.setFeedbackDesc,
    createMore: state.createMore,
    setCreateMore: state.setCreateMore,
  }));

  const [bookmarkModalStatus, setBookmarkModalStatus] =
    useState<BookmarkModalState>({
      status: false,
      action: 'add',
      target: '',
    });
  const [selectedBookmark, setSelectedBookmark] = useState<BookmarkT | null>();

  const { changeCustomerAttr } = useCustomerStore((state) => ({
    changeCustomerAttr: state.changeCustomerAttr,
  }));

  const setCurrDropdown = useDropdownStore((state) => state.setCurrDropdown);

  const addDefaultToast = useToastStore((state) => state.addDefaultToast);

  const { tagMap } = useTagStore((state) => ({
    tagMap: state.tagMap,
  }));

  const [isSaving, setIsSaving] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [attachments, setAttachment] = useState<AttachmentT[]>([]);

  const [inputs, onChange, onCustomChange] = useInputs({
    title: '',
    date: getTimezoneDayjs().format('YYYY-MM-DDTHH:mm:ss'),
    rating_score: undefined,
    nps_score: undefined,
    ces_score: undefined,
    csat_score: undefined,
  });

  const { state, setValue } = useStates<State>({
    tagList: [],
    company: null,
    customer: null,
    priority: undefined,
    data_origin: undefined,
    agent: undefined,
    bookmarks: [],
    remarkList: [],
  });

  const autoSuggestHandler = (rawText: string) => {
    setSuggestions([]);
    const suggestions = [] as string[];
    Object.values(tagMap).forEach((tag) => {
      const check = /[a-zA-Z]/;
      const onlyEnglish = check.test(tag.name);
      if (onlyEnglish ? tag.name.length < 3 : tag.name.length < 2) return;
      if (rawText.includes(tag.name)) {
        const index = rawText.indexOf(tag.name);
        const endIndex = index + tag.name.length;
        if (
          (index === 0 ||
            rawText[index - 1] === ' ' ||
            rawText[index - 1] === '\n') &&
          (endIndex === rawText.length ||
            rawText[endIndex] === '\n' ||
            rawText[endIndex] === ' ')
        )
          suggestions.push(tag.alias);
      }
    });
    if (suggestions.length) {
      setSuggestions(suggestions);
    }
  };

  const handleOnChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.currentTarget.value;
    setDescription(text);
    // TODO: Debounce or Throttle
    autoSuggestHandler(text);
  };

  const { addNewFeedback } = useFeedbackListStore((state) => ({
    addNewFeedback: state.addNewFeedback,
  }));

  useEffect(() => {
    if (bookmarkModalStatus.target === '') {
      setSelectedBookmark(null);
      return;
    }
    const bookmark = state.bookmarks?.filter(
      (item) => item.alias === bookmarkModalStatus.target,
    );

    if (bookmark && bookmark.length > 0) setSelectedBookmark(bookmark[0]);
  }, [bookmarkModalStatus, state.bookmarks]);

  const toggleItem = (
    item: string,
    list: string[],
    setter: (list: string[]) => void,
  ) => {
    let newList = [...list];
    if (newList.includes(item)) {
      const idx = newList.indexOf(item);
      if (idx > -1) newList.splice(idx, 1);

      setter(newList);
    } else {
      newList.push(item);

      setter(newList);
    }
  };

  const { setPopup } = usePopupStore((state) => ({ setPopup: state.setPopup }));

  useEffect(() => {
    if (bookmarkModalStatus.action === 'delete')
      setPopup({
        popupTheme: 'negative',
        type: 'confirm',
        title: 'Delete the link',
        message:
          'Are you sure you want to delete this link? This cannot be undone.',
        confirmText: 'Delete',
        onConfirm: () => {
          setValue(
            'bookmarks',
            state.bookmarks.filter(
              (item) => item.alias !== bookmarkModalStatus.target,
            ),
          );
          setBookmarkModalStatus({
            status: false,
            action: undefined,
            target: '',
          });
        },
        onCancel: () =>
          setBookmarkModalStatus({
            status: false,
            action: undefined,
            target: '',
          }),
      });
  }, [bookmarkModalStatus, setValue, setPopup, state.bookmarks]);

  const onSave = async () => {
    try {
      if (isSaving) return;
      setIsSaving(true);
      const req: PostFeedbackParams = {
        title: inputs.title,
        raw_text: description,
        text: '',
        date: getTimezoneDayjs(inputs.date).toJSON(),
        company: state.company?.alias,
        customer: state.customer?.alias,
        attachments: attachments,
        bookmarks: state.bookmarks,
        remarks: state.remarkList,
      };

      if (!req.raw_text)
        return addDefaultToast('Feedback must be filled', 'fail');

      if (state.tagList.length > 0) {
        req.labels = state.tagList;
      }
      if (state.priority) {
        req.priority = +state.priority as PriorityKeyT;
      }
      if (state.data_origin) {
        req.data_origin = state.data_origin;
      }
      if (state.agent) {
        req.manager = state.agent;
      }

      if (inputs.rating_score) {
        req.rating_score = Number(inputs.rating_score);
      }

      if (inputs.nps_score) {
        req.nps_score = Number(inputs.nps_score);
      }

      if (inputs.ces_score) {
        req.ces_score = Number(inputs.ces_score);
      }

      if (inputs.csat_score) {
        req.csat_score = Number(inputs.csat_score);
      }

      const result = await addNewFeedback(req);

      // Track feedback manual create complete event
      track(AMP_KEYS.FEEDBACK_MANUAL_CREATE_COMPLETE, {
        feedback_alias: result?.alias || 'unknown',
        has_tags: req.labels && req.labels.length > 0,
        has_sentiment: false, // sentiment is auto-analyzed
      });

      setDescription('');
      setIsSaving(false);
      props.close();
      if (createMore)
        setTimeout(() => {
          navigate(
            `${location.pathname}${addQuery(
              Object.fromEntries([...searchParams]),
              {
                key: 'feedback-create',
                value: 'true',
              },
            )}`,
          );
        }, 500);
    } catch (e) {
      console.error(e);
    }
  };

  const onDeleteAttachment = (alias: string) => {
    setAttachment((state) => state.filter((a) => a.alias !== alias));
  };

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return current && current > getTimezoneDayjs().startOf('day');
  };

  const companyChangeHandler = (
    company: CompanyT | null,
    prevState?: {
      customer?: (CustomerInfoT & { alias: string }) | null;
      company?: (CompanyInfoT & { alias: string }) | null;
    },
  ) => {
    setValue('company', company);
    if (!prevState?.company && prevState?.customer) {
      changeCustomerAttr(
        prevState?.customer.alias,
        'company_info',
        company
          ? {
              alias: company.alias,
              name: company.name,
            }
          : undefined,
      );
      return;
    }
    setValue('customer', null);
    setTimeout(() => {
      setCurrDropdown('feedback-create-customer-dropdown');
    }, 10);
  };

  const customerChangeHandler = async (
    _: string,
    customer: CustomerT | null,
  ) => {
    setValue('customer', customer);
    if (customer && state.company && !customer?.company_info) {
      // 선택된 company가 있는데, 선택한 customer가 소속된 회사가 없는경우
      // 해당 company에 customer를 소속시킴
      changeCustomerAttr(customer.alias, 'company_info', state.company);
      return;
    }
    if (customer?.company_info) {
      // 선택한 customer가 이미 소속된 company가 있는 경우
      // 해당 feedback에 해당 company 오버라이드
      const company = await service.account.fetchCompany(
        customer.company_info.alias,
      );
      setValue('company', company);
      return;
    }
  };

  useEffect(() => {
    const arr = Object.keys(tagMap);
    const len = arr.length;
    for (let i = 0; i < len; i++) {
      const key = arr[i];
      if (
        tagMap[key].name === DEFAULT_SOURCE_MANUAL &&
        !state.tagList.includes(key)
      ) {
        setValue('tagList', state.tagList.concat([key]));
        break;
      }
    }
  }, [tagMap, setValue, state.tagList]);

  useKeymap('Escape', props.close);

  return (
    <Style>
      <ScrollView
        style={{ height: 'calc(100% - 64px)', padding: '32px 24px 0 24px' }}
      >
        <VFlex>
          <Flex a="center" j="space-between">
            <Heading type="h2">Add new feedback</Heading>
            <IconButton
              icon={<CloseOutlined />}
              onClick={props.close}
              tooltip="Close"
              shortcut="ESC"
            />
          </Flex>

          <Space h="32px" />
          <FlexibleTitleInput
            initFocus
            name="title"
            value={inputs.title}
            onChange={onChange}
            placeholder="Type feedback title"
          />

          <Space h="16px" />

          <FlexibleTextarea
            showBorder
            placeholder="Type feedback body"
            value={description}
            onChange={handleOnChange}
            minHeight={150}
          />

          <Space h="24px" />

          {attachments.length > 0 && (
            <>
              <Attachment
                attachments={attachments}
                onDeleteAttachment={onDeleteAttachment}
              />
              <Space h="24px" />
            </>
          )}

          <VFlex className="property-list-area" g={5}>
            <Flex a="flex-start">
              <AttrText className="property-label" style={{ marginTop: '5px' }}>
                Tag
              </AttrText>
              <TagSelector
                name={`feedback-create-tag-dropdown`}
                selectedTags={state.tagList}
                toggleItem={(tag) =>
                  toggleItem(tag, state.tagList, (list) => {
                    setValue('tagList', list);
                  })
                }
                suggestions={suggestions}
                isCreate={true}
              />
            </Flex>
            <PropertyRow>
              <AttrText className="property-label">Customer</AttrText>
              <Flex a="center">
                <CompanyDropdown
                  clearable
                  name={`feedback-create-company-dropdown`}
                  company_info={state.company}
                  customer_info={
                    state.customer && {
                      name: state.customer.name,
                      alias: state.customer.alias,
                      email: state.customer.email,
                    }
                  }
                  changeCompany={companyChangeHandler}
                  view="detail"
                />
                &nbsp;/&nbsp;
                <CustomerDropdown
                  name={`feedback-create-customer-dropdown`}
                  feedbackAlias="createFeedback"
                  customer_info={
                    state.customer && {
                      name: state.customer.name,
                      alias: state.customer.alias,
                      email: state.customer.email,
                    }
                  }
                  company_info={state.company}
                  changeCustomer={customerChangeHandler}
                  view="detail"
                />
              </Flex>
            </PropertyRow>
            <PropertyRow>
              <AttrText className="property-label">Priority</AttrText>
              <PriorityDropdown
                name={`feedback-create-priority-dropdown`}
                selected={state.priority || 1}
                onSelect={(priorityKey) => setValue('priority', priorityKey)}
              />
            </PropertyRow>
            <PropertyRow>
              <AttrText className="property-label">Data Origin</AttrText>
              <DataOriginDropdown
                selected={state.data_origin || NOT_ASSIGNED_LABEL}
                name={`feedback-create-dataorigin-dropdown`}
                onSelect={(key) => {
                  setValue('data_origin', key);
                }}
                onClear={() => {
                  setValue('data_origin', undefined);
                }}
              />
            </PropertyRow>
            <Flex style={{ minHeight: 0 }} a="flex-start" g={4}>
              <AttrText className="property-label" style={{ marginTop: 10 }}>
                Remarks
              </AttrText>
              <RemarkInput
                list={state.remarkList}
                onAdd={
                  (item, list) => setValue('remarkList', list)
                  // changeProduct(feedbackAlias, list, item, 'add')
                }
                onErase={
                  (item, list) => setValue('remarkList', list)
                  // changeProduct(feedbackAlias, list, item, 'remove')
                }
                style={{
                  flex: '1',
                }}
              />
            </Flex>
            <PropertyRow>
              <AttrText className="property-label">Rating score</AttrText>
              <AttrNumberInput
                name="rating_score"
                value={inputs.rating_score}
                placeholder="Feedback rating score"
                onChange={(e) => {
                  let value = Number(e.target.value);
                  if (value < 0) {
                    value = 0;
                  } else if (value > 10) {
                    value = 10;
                  }
                  value = Math.floor(value);
                  onChange({ target: { name: 'rating_score', value } });
                }}
              />
            </PropertyRow>
            <PropertyRow>
              <AttrText className="property-label">NPS score</AttrText>
              <AttrNumberInput
                name="nps_score"
                value={inputs.nps_score}
                placeholder="Feedback nps score"
                onChange={(e) => {
                  let value = Number(e.target.value);
                  if (value < 0) {
                    value = 0;
                  } else if (value > 10) {
                    value = 10;
                  }
                  value = Math.floor(value);
                  onChange({ target: { name: 'nps_score', value } });
                }}
              />
            </PropertyRow>
            <PropertyRow>
              <AttrText className="property-label">CES score</AttrText>
              <AttrNumberInput
                name="nps_score"
                value={inputs.ces_score}
                placeholder="Feedback ces score"
                onChange={(e) => {
                  let value = Number(e.target.value);
                  if (value < 0) {
                    value = 0;
                  } else if (value > 10) {
                    value = 10;
                  }
                  value = Math.floor(value);
                  onChange({ target: { name: 'ces_score', value } });
                }}
              />
            </PropertyRow>
            <PropertyRow>
              <AttrText className="property-label">CSAT score</AttrText>
              <AttrNumberInput
                name="csat_score"
                value={inputs.csat_score}
                placeholder="Feedback csat score"
                onChange={(e) => {
                  let value = Number(e.target.value);
                  if (value < 0) {
                    value = 0;
                  } else if (value > 10) {
                    value = 10;
                  }
                  value = Math.floor(value);
                  onChange({ target: { name: 'csat_score', value } });
                }}
              />
            </PropertyRow>
            <PropertyRow>
              <AttrText className="property-label">Agent</AttrText>
              <AgentDropdown
                selected={state.agent || NOT_ASSIGNED_LABEL}
                name={`feedback-create-agent-dropdown`}
                onSelect={(key) => {
                  setValue('agent', key);
                }}
                onClear={() => {
                  setValue('agent', undefined);
                }}
              />
            </PropertyRow>
            <PropertyRow>
              <AttrText className="property-label">Add related links</AttrText>
              <Bookmark
                bookmarkList={state.bookmarks}
                setBookmarkModalStatus={setBookmarkModalStatus}
              />
            </PropertyRow>
            <PropertyRow>
              <AttrText className="property-label">Date</AttrText>
              <StyledDatePicker
                name="date"
                onChange={(_, dataString) => onCustomChange('date', dataString)}
                disabledDate={disabledDate}
                getPopupContainer={(triggerNode) => {
                  return triggerNode.parentNode as HTMLDivElement;
                }}
                value={getTimezoneDayjs(inputs.date)}
                format={DATE_FORMAT.readable}
              />
            </PropertyRow>
          </VFlex>

          <Space h="24px" />

          <Modal
            status={
              bookmarkModalStatus.status &&
              bookmarkModalStatus.action !== 'delete'
            }
            closeModal={() =>
              setBookmarkModalStatus({
                status: false,
                action: undefined,
                target: '',
              })
            }
            layerId="syncly-popup-layer"
          >
            <BookmarkCreateModal
              type="feedback"
              link={selectedBookmark?.link}
              name={selectedBookmark?.name}
              isEdit={bookmarkModalStatus.action === 'edit'}
              onClick={(bookmark: PostBookmarkParams) => {
                if (bookmarkModalStatus.action === 'edit') {
                  setValue(
                    'bookmarks',
                    state.bookmarks?.map((item) => {
                      if (item.alias === bookmarkModalStatus.target)
                        return { alias: item.alias, ...bookmark };
                      return item;
                    }),
                  );
                } else {
                  setValue('bookmarks', [
                    ...state.bookmarks,
                    { ...bookmark, alias: generateRandomString() },
                  ]);
                }
              }}
            />
          </Modal>
        </VFlex>
      </ScrollView>
      <VFlex className="action-float">
        <Divider direction="h" />
        <Flex g="16px" j="flex-end" style={{ padding: '16px 0' }}>
          <Flex a="center" g="8px">
            <ToggleRadio status={createMore} onChange={setCreateMore} />
            <Typo type="label2" color={color.gray600}>
              Create more
            </Typo>
          </Flex>
          <Button variant="primary" onClick={onSave} sizeType="m">
            Create feedback
          </Button>
        </Flex>
      </VFlex>
    </Style>
  );
};

export default CreateFeedbackModal;

const Style = styled.div`
  position: relative;
  height: 100%;
  .company-selector-wrapper {
    height: 38px;
    position: relative;
    cursor: pointer;
    background-color: ${(props) => props.theme.colors.gray100};
    transition: all 0.2s;
    padding: 0 11px;
    border-radius: 4px;
    .edit-icon {
      opacity: 0;
      transform: 0.2s;
    }
    &:hover {
      background-color: ${(props) => props.theme.colors.gray200};
      .edit-icon {
        opacity: 1;
      }
    }
  }
  .property-label {
    width: 116px;
  }
  .action-float {
    bottom: 0;
    right: 0;
    padding: 0px 32px;
  }
`;

const PropertyRow = styled(Flex)`
  min-height: 26px;
  height: 32px;
  gap: 4px;
  align-items: center;
`;
